import { AiChatErrorCode } from './models/ai-chat-error-code.enum';
import { AiChatError } from './models/ai-chat-error.interface';

const HTTP_TOO_MANY_REQUESTS = 429;

const NON_RETRYABLE_CODES: string[] = [
	AiChatErrorCode.BUDGET_EXCEEDED,
	AiChatErrorCode.VALIDATION_ERROR,
	AiChatErrorCode.FORBIDDEN,
	AiChatErrorCode.UNAUTHORIZED,
];

function readParams(params: unknown): Pick<AiChatError, 'resetAt' | 'retryAfter'> {
	if (!params || typeof params !== 'object') return {};
	const { resetAt, retryAfter } = params as { resetAt?: unknown; retryAfter?: unknown };
	return {
		resetAt: typeof resetAt === 'string' && resetAt ? resetAt : undefined,
		retryAfter: typeof retryAfter === 'number' ? retryAfter : undefined,
	};
}

function parseFlatErrorBody(body: Record<string, unknown>, status: number): AiChatError | null {
	if (typeof body['error'] !== 'string' || !body['error']) return null;
	const message = typeof body['message'] === 'string' ? body['message'] : body['error'];
	if (status !== HTTP_TOO_MANY_REQUESTS) return { code: '', message };

	return {
		code: AiChatErrorCode.REQUEST_RATE_LIMITED,
		message,
		retryAfter: typeof body['retryAfter'] === 'number' ? body['retryAfter'] : undefined,
	};
}

export function parseAgentErrorResponse(body: unknown, status: number): AiChatError | null {
	if (!body || typeof body !== 'object') return null;
	const error = (body as { error?: unknown }).error;

	if (!error || typeof error !== 'object') {
		return parseFlatErrorBody(body as Record<string, unknown>, status);
	}

	const { message, code, params } = error as {
		message?: unknown;
		code?: unknown;
		params?: unknown;
	};

	return {
		code: typeof code === 'string' ? code : '',
		message: typeof message === 'string' ? message : '',
		...readParams(params),
	};
}

export function parseAgentErrorFrame(json: unknown): AiChatError | null {
	if (!json || typeof json !== 'object') return null;
	const { error, code, params } = json as {
		error?: unknown;
		code?: unknown;
		params?: unknown;
	};
	if (typeof error !== 'string' || !error) return null;

	return {
		code: typeof code === 'string' ? code : '',
		message: error,
		...readParams(params),
	};
}

/** Unknown codes count as transient on purpose — the backend may add codes we don't know yet. */
export function isRetryableAgentError(error: AiChatError): boolean {
	return !NON_RETRYABLE_CODES.includes(error.code);
}
